import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Trophy, Send, Mail, X, Plus, Users, ExternalLink, GitCompare, FileText, Download } from "lucide-react";
import { Button } from "../ui/button";
import NovaCriteriaRadar from "./NovaCriteriaRadar";
import NovaCriteriaRadarCompare from "./NovaCriteriaRadarCompare";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NovaShortlistPanel — top 5 retos por score del comité + botón notificar.
 * Props:
 *   campaign: {id, name, committee_emails, invitees, voting_criteria}
 *   token
 *   onReload
 */
export default function NovaShortlistPanel({ campaign, token, onReload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [emails, setEmails] = useState(() => (campaign.committee_emails || []).join(", "));
  const [notifying, setNotifying] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);
  const [compareIds, setCompareIds] = useState(new Set());
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 3) next.add(id);
      else toast.error("Máximo 3 retos para comparar");
      return next;
    });
  };

  const downloadMinutes = async () => {
    setDownloadingPdf(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaign.id}/committee-minutes.pdf?top=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "No se pudo generar el PDF");
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NOVA_Acta_Comite_${(campaign.name || "campania").replace(/\W+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("📄 Acta del comité descargada");
    } catch (e) {
      toast.error(e.message || "Error generando acta");
    } finally { setDownloadingPdf(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaign.id}/shortlist?top=5`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [campaign.id, campaign.challenges?.length]);

  const parsedEmails = useMemo(() => (
    emails.split(/[,;\s\n]+/).map(e => e.trim().toLowerCase()).filter(e => /@/.test(e))
  ), [emails]);

  const saveEmails = async () => {
    setSavingEmails(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaign.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ committee_emails: parsedEmails }),
      });
      if (!r.ok) throw new Error();
      toast.success(`${parsedEmails.length} miembros del comité guardados`);
      onReload && onReload();
    } catch { toast.error("Error guardando emails"); }
    finally { setSavingEmails(false); }
  };

  const notify = async () => {
    if (!parsedEmails.length) { toast.error("Añade al menos un email del comité"); return; }
    setNotifying(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaign.id}/notify-committee`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: parsedEmails,
          only_pending: true,
          public_base_url: window.location.origin,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "error");
      toast.success(`📧 Notificados ${d.sent}/${d.total_recipients} miembros del comité (${d.skipped} sin retos pendientes)`);
      setNotifyOpen(false);
    } catch (e) {
      toast.error(e.message || "Error notificando");
    } finally { setNotifying(false); }
  };

  const list = data?.shortlist || [];
  const criteria = data?.criteria || campaign.voting_criteria || [];

  return (
    <div className="rounded-2xl border border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 via-white to-white dark:from-amber-500/10 dark:via-transparent dark:to-transparent p-4 md:p-5 space-y-3" data-testid="shortlist-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center">
            <Trophy className="w-4 h-4"/>
          </div>
          <div>
            <div className="text-sm font-black text-amber-900 dark:text-amber-200">Ranking Shortlist Comité</div>
            <div className="text-[11px] text-muted-foreground">Top 5 retos por score ponderado · para reunión de decisión</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="text-xs" data-testid="shortlist-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin"/> : "Refrescar"}
          </Button>
          <Button size="sm" variant="outline" onClick={downloadMinutes} disabled={downloadingPdf || !list.length} className="text-xs gap-1.5" data-testid="download-committee-pdf">
            {downloadingPdf ? <Loader2 className="w-3 h-3 animate-spin"/> : <FileText className="w-3.5 h-3.5"/>}
            Exportar Acta PDF
          </Button>
          <Button size="sm" onClick={() => setNotifyOpen(!notifyOpen)} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 text-xs" data-testid="notify-committee-toggle">
            <Mail className="w-3.5 h-3.5"/> Notificar Comité
          </Button>
        </div>
      </div>

      {notifyOpen && (
        <div className="rounded-xl border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-slate-900 p-3 space-y-2" data-testid="notify-committee-form">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1"><Users className="w-3.5 h-3.5"/> Miembros del comité</div>
            <button onClick={() => setNotifyOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
          </div>
          <textarea
            rows={2} value={emails} onChange={(e) => setEmails(e.target.value)}
            placeholder="ana@valorizasm.com, cristina@valorizasm.com, ..."
            className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-xs"
            data-testid="committee-emails-input"
          />
          <div className="text-[10px] text-muted-foreground">
            {parsedEmails.length} email{parsedEmails.length !== 1 ? "s" : ""} válidos · Se enviará solo los retos GO pendientes de voto de cada destinatario.
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={saveEmails} disabled={savingEmails || !parsedEmails.length} className="text-xs" data-testid="save-committee-emails">
              {savingEmails ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <Plus className="w-3 h-3 mr-1"/>}
              Guardar en campaña
            </Button>
            <Button size="sm" onClick={notify} disabled={notifying || !parsedEmails.length} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs gap-1" data-testid="notify-committee-send">
              {notifying ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>}
              Enviar a {parsedEmails.length}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-4">Calculando shortlist…</div>
      ) : !list.length ? (
        <div className="text-center text-muted-foreground text-xs py-6 border border-dashed border-border rounded-xl">
          Aún no hay votos del comité. Mueve algún reto a la fase <strong>Comité</strong> y pide a los miembros que voten.
        </div>
      ) : (
        <>
        {compareIds.size >= 2 && (
          <div className="rounded-xl border border-violet-300 dark:border-violet-700 bg-violet-50/60 dark:bg-violet-500/5 p-3 space-y-2" data-testid="compare-panel">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-violet-800 dark:text-violet-300 flex items-center gap-1.5">
                <GitCompare className="w-3.5 h-3.5"/> Comparador de Retos ({compareIds.size})
              </div>
              <Button size="sm" variant="ghost" onClick={() => setCompareIds(new Set())} className="text-xs h-6" data-testid="compare-clear">
                <X className="w-3 h-3 mr-1"/>Limpiar selección
              </Button>
            </div>
            <NovaCriteriaRadarCompare
              items={list.filter(i => compareIds.has(i.id)).map(i => ({ label: `#${i.n} · ${(i.title || "").slice(0, 40)}`, score: i.score }))}
              criteria={criteria}
              height={340}
            />
          </div>
        )}
        <div className="space-y-2">
          {list.map((item, idx) => {
            const selected = compareIds.has(item.id);
            return (
            <div key={item.id} className={`rounded-xl border p-3 flex flex-col md:flex-row gap-3 items-start transition-all ${selected ? "border-violet-400 bg-violet-50/40 dark:bg-violet-500/10" : "border-amber-200 dark:border-amber-800/60 bg-white dark:bg-slate-900/60"}`} data-testid={`shortlist-row-${idx}`}>
              <label className="flex items-center gap-1 cursor-pointer flex-shrink-0 pt-1" title="Comparar (max 3)">
                <input
                  type="checkbox" checked={selected} onChange={() => toggleCompare(item.id)}
                  className="accent-violet-500 w-4 h-4"
                  data-testid={`compare-check-${idx}`}
                />
              </label>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-white flex-shrink-0 ${idx === 0 ? "bg-amber-500" : idx === 1 ? "bg-slate-400" : idx === 2 ? "bg-amber-700" : "bg-slate-500"}`}>
                  {idx + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <code className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">#{item.n}</code>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 font-bold">{item.score?.overall}/5</span>
                    <span className="text-[10px] text-muted-foreground">{item.voters} votante{item.voters !== 1 ? "s" : ""}</span>
                    {item.area && <span className="text-[10px] text-muted-foreground">· {item.area}</span>}
                  </div>
                  <div className="text-sm font-semibold leading-snug mt-0.5">{item.title}</div>
                  {item.champion && <div className="text-[11px] text-muted-foreground">👤 {item.champion}</div>}
                </div>
              </div>
              <div className="w-full md:w-48 flex-shrink-0" title="Radar promedio del comité">
                <NovaCriteriaRadar score={item.score} criteria={criteria} height={140}/>
              </div>
            </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}
